import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { Label, Select, SelectContent, SelectItem, SelectTrigger, SelectValue, SpecraBadge, SpecraButton } from '@mono/ui-components';
import { Testids } from '@mono/ui-selectors';
import { useCallback, useMemo, useRef, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { toast } from 'sonner';
import { FileUploader } from '../../../../components/ProjectList/FileUploader';
import { useUploadArtifacts } from '../../../../hooks/useDeliveryDocuments';
import { useProgramContext } from '../../../../layouts/ProgramLayout';
import { ConfirmDeleteDialog } from '../../_shared/components/ConfirmDeleteDialog';
import { DrawerDocumentsSection } from '../../_shared/components/DrawerDocumentsSection';
import { EntityFormDialog } from '../../_shared/components/EntityFormDialog';
import { EntityTable } from '../../_shared/components/EntityTable';
import { ProgramDocumentPicker } from '../../_shared/components/ProgramDocumentPicker';
import { ScoreDetailDrawer } from '../../_shared/components/ScoreDetailDrawer';
import { DEFAULT_COMPANY_DOCUMENT_CATEGORY, DOCUMENT_CATEGORY_OPTIONS, SUBCONTRACT_DOCUMENT_CATEGORY, TEAMING_AGREEMENT_DOCUMENT_CATEGORY } from '../../_shared/constants/documentCategories';
import { useTenantId } from '../../_shared/hooks/useTenantId';
import { useCreateProgramDocument, useProgramDocuments } from '../../Docs/hooks';
import { companyProfileHooks } from '../../Registries/hooks';
import { useProgramWriteAccess } from '../programWriteAccess';
import { useOwnCompanyProfile } from '../referenceDataHooks';
import { programCompanyHooks, programCompanyStaffingLineHooks } from '../relationshipHooks';
import { CompanyStaffingDrawer } from './CompanyStaffingDrawer';
const typeOptions = [
    {
        value: 'Prime',
        label: 'Prime'
    },
    {
        value: 'Sub',
        label: 'Sub'
    },
    {
        value: 'Consultant',
        label: 'Consultant'
    }
];
export function ProgramCompaniesTab({ programId }) {
    const { program } = useProgramContext();
    const canEditPolicy = useProgramWriteAccess(programId).roster;
    const tenantId = useTenantId();
    const companiesQuery = companyProfileHooks.useList({
        companyId: tenantId
    });
    const { data, isLoading } = programCompanyHooks.useList({
        programId
    });
    const upsert = programCompanyHooks.useUpsert();
    const remove = programCompanyHooks.useDelete();
    // SAD-132: staffing lines hang off company_assignment_id, a SUBCONTRACTOR
    // assignment row — the tenant's own company usually has no assignment on a
    // program (assignments only come from contract-extraction parties or a
    // manual Add here), so an internal engineer or support person has no
    // staffing home at all. Rather than making company_assignment_id nullable
    // or adding a second contact-to-LCAT model, this seeds the tenant's own
    // company as an ordinary Prime assignment — same row shape, same drawer,
    // same FTE rollups, same vacancy model, zero schema change. Explicit and
    // discoverable (not auto-seeded) so an untouched program's numbers never
    // move on their own; see ownCompany.data guard below.
    const ownCompany = useOwnCompanyProfile();
    const [seedingPrime, setSeedingPrime] = useState(false);
    // One list call for the whole tab, filtered by program — not one per row —
    // then grouped client-side by company_assignment_id for the line count
    // (disables the FTE/Vacancies inputs).
    const { data: linesData } = programCompanyStaffingLineHooks.useList({
        programId
    });
    const linesByAssignment = useMemo(()=>{
        const m = new Map();
        for (const line of linesData?.items ?? []){
            const list = m.get(line.companyAssignmentId);
            if (list) list.push(line);
            else m.set(line.companyAssignmentId, [
                line
            ]);
        }
        return m;
    }, [
        linesData
    ]);
    const [staffingAssignment, setStaffingAssignment] = useState(null);
    // The optional document lands as a company-scoped program document (the
    // system of record), exactly as the Record Document form does. The program
    // is still fetched for the staffing drawer's LCAT picker (zou workaround,
    // replaced by the LCAT registry).
    const upload = useUploadArtifacts();
    const createDoc = useCreateProgramDocument();
    const docsQuery = useProgramDocuments(programId);
    // assignmentId -> categories of the documents linked to it, for the
    // missing-paperwork pill in the Company column.
    const categoriesByAssignment = useMemo(()=>{
        const map = new Map();
        for (const d of docsQuery.data?.items ?? []){
            if (!d.companyAssignmentId) continue;
            const set = map.get(d.companyAssignmentId) ?? new Set();
            set.add(d.category);
            map.set(d.companyAssignmentId, set);
        }
        return map;
    }, [
        docsQuery.data
    ]);
    const recordStatus = program?.recordStatusLabel?.trim().toLowerCase();
    const [formOpen, setFormOpen] = useState(false);
    const [deleting, setDeleting] = useState(null);
    const [fileId, setFileId] = useState('');
    // A locally staged file, uploaded only on submit — mirrors the Record
    // Document form so cancelling the dialog or a failed add records nothing.
    // Mutually exclusive with the picker: one document references one file.
    const [stagedFiles, setStagedFiles] = useState([]);
    const [documentCategory, setDocumentCategory] = useState(DEFAULT_COMPANY_DOCUMENT_CATEGORY);
    // Remembers the assignment created earlier in THIS dialog session, keyed by the
    // company it was created for. If the company upsert succeeds but the document
    // step then throws, the dialog stays open on the error; a plain retry would
    // send an empty id and the server would INSERT a second row. Reusing the id
    // makes the retry an update — but only while the company still matches, since
    // the server's update path ignores companyProfileId, so reusing it across a
    // company change would silently keep the old company. Cleared on (re)open and
    // full success.
    const createdAssignmentRef = useRef(null);
    // Files whose artifact upload already succeeded but whose ProgramDocument
    // hasn't been recorded yet — e.g. one file's record call threw while
    // others succeeded. A retry replays exactly these instead of re-uploading,
    // same idea as createdAssignmentRef above, extended to N files.
    const pendingDocumentsRef = useRef([]);
    const resetFile = ()=>{
        setFileId('');
        setStagedFiles([]);
        setDocumentCategory(DEFAULT_COMPANY_DOCUMENT_CATEGORY);
        createdAssignmentRef.current = null;
        pendingDocumentsRef.current = [];
    };
    const companies = companiesQuery.data?.items ?? [];
    const companyById = useMemo(()=>{
        const m = new Map();
        for (const c of companies)m.set(c.id, c);
        return m;
    }, [
        companies
    ]);
    // Workshare is each company's share of the program's CURRENT staffing, so the
    // denominator sums only active assignments — an inactive/departed company no
    // longer carries effort and must not dilute the active companies' shares.
    const assignments = data?.items ?? [];
    // Whether the tenant's own company already has an assignment on this
    // program — the button below is hidden once true, which is what makes
    // invoking the affordance twice a no-op from the UI (the server-side
    // upsert-by-(program_id, company_profile_id) natural key is the real
    // backstop against a genuine double-click race).
    const primeAssignment = ownCompany.data ? assignments.find((a)=>a.companyProfileId === ownCompany.data?.id) ?? null : null;
    const handleAddPrime = async ()=>{
        if (!ownCompany.data?.id || seedingPrime) return;
        setSeedingPrime(true);
        try {
            await upsert.mutateAsync({
                id: '',
                programId,
                companyProfileId: ownCompany.data.id,
                active: true,
                type: 'Prime',
                // Zero on purpose: seeding must not change any existing company's
                // Workshare % on this program. Real headcount is added afterward via
                // staffing lines, which is a deliberate, visible follow-up action —
                // never a side effect of adding the staffing home itself.
                fteCount: 0,
                vacancies: 0
            });
            toast.success(`${ownCompany.data.name || 'Your organization'} added as Prime`);
        } catch (err) {
            toast.error(err instanceof Error ? err.message : 'Failed to add your organization as Prime');
        } finally{
            setSeedingPrime(false);
        }
    };
    // Re-resolves against the live list on every render so the drawer's own FTE
    // summary re-derives after a write too — `staffingAssignment` itself is a
    // snapshot taken when the row was clicked and would otherwise go stale the
    // moment the invalidated `program company` list refetches.
    const staffingAssignmentLive = staffingAssignment ? assignments.find((a)=>a.id === staffingAssignment.id) ?? staffingAssignment : null;
    const totalFte = useMemo(()=>assignments.reduce((sum, a)=>sum + (a.active ? a.fteCount || 0 : 0), 0), [
        assignments
    ]);
    // Store only the selected id and DERIVE the record from `assignments` — so an
    // open drawer always reflects the latest list (e.g. after an inline-edit Save)
    // without an effect that re-syncs state. Syncing via effect infinite-loops
    // when the list returns fresh-but-equal references each render.
    const [detailId, setDetailId] = useState(null);
    // A link (?company=<companyProfileId>, e.g. from chat) opens that company's
    // details; closing the drawer drops the parameter.
    const [searchParams, setSearchParams] = useSearchParams();
    const linkedCompany = searchParams.get('company') ?? '';
    const linkedId = linkedCompany ? assignments.find((a)=>a.companyProfileId === linkedCompany)?.id ?? null : null;
    const openId = detailId ?? linkedId;
    const detail = openId ? assignments.find((a)=>a.id === openId) ?? null : null;
    const closeDetail = useCallback(()=>{
        setDetailId(null);
        if (searchParams.has('company')) {
            const next = new URLSearchParams(searchParams);
            next.delete('company');
            setSearchParams(next, {
                replace: true
            });
        }
    }, [
        searchParams,
        setSearchParams
    ]);
    // Whether the detail drawer opens in read or straight into edit (row Edit).
    const [detailMode, setDetailMode] = useState('read');
    // Company is a parent-controlled control (single source of truth), mirroring
    // RisksTab's owner picker: the drawer's editControl fills it and the submit
    // reads it. Kept out of the drawer's RHF form so picking a company never
    // remounts/resets the other fields. Seeded from the record on edit start.
    const [editCompanyProfileId, setEditCompanyProfileId] = useState('');
    // A single company (Add dialog); the create form's fields are static because a
    // brand-new assignment can never have staffing lines yet, so FTE/Vacancies are
    // always hand-typed there.
    const createFields = useMemo(()=>[
            {
                kind: 'select',
                name: 'companyProfileId',
                label: 'Company',
                required: true,
                options: companies.map((c)=>({
                        value: c.id,
                        label: c.name
                    }))
            },
            {
                kind: 'select',
                name: 'type',
                label: 'Type',
                required: true,
                options: typeOptions
            },
            {
                kind: 'number',
                name: 'fteCount',
                label: 'FTE count',
                min: 0,
                step: 0.5
            },
            {
                kind: 'number',
                name: 'vacancies',
                label: 'Vacancies',
                min: 0,
                step: 1
            },
            {
                kind: 'checkbox',
                name: 'active',
                label: 'Active assignment'
            }
        ], [
        companies
    ]);
    // Active programs need a subcontract and Draft ones a teaming agreement on
    // every active non-Prime row; any other status requires nothing.
    const missingPaperwork = useCallback((row)=>{
        if (!row.active || row.type === 'Prime') return null;
        const have = categoriesByAssignment.get(row.id);
        if (recordStatus === 'active' && !have?.has(SUBCONTRACT_DOCUMENT_CATEGORY)) {
            return 'SubK missing';
        }
        if (recordStatus === 'draft' && !have?.has(TEAMING_AGREEMENT_DOCUMENT_CATEGORY)) {
            return 'Teaming Agreement missing';
        }
        return null;
    }, [
        categoriesByAssignment,
        recordStatus
    ]);
    const columns = useMemo(()=>[
            {
                key: 'companyName',
                header: 'Company',
                render: (row)=>{
                    const name = row.companyName || companyById.get(row.companyProfileId)?.name || '—';
                    const missing = missingPaperwork(row);
                    return /*#__PURE__*/ _jsxs("span", {
                        className: "flex items-center gap-2",
                        children: [
                            /*#__PURE__*/ _jsx("span", {
                                "data-testid": Testids.deliveryProgramRel.companyName,
                                children: name
                            }),
                            missing && /*#__PURE__*/ _jsx(SpecraBadge, {
                                variant: "danger",
                                "data-testid": Testids.deliveryProgramRel.companyPaperworkMissing,
                                children: missing
                            })
                        ]
                    });
                }
            },
            {
                key: 'type',
                header: 'Type'
            },
            {
                key: 'fteCount',
                header: 'FTE',
                // The drilldown entry point named in the spec: click the number to
                // open the staffing drawer, whether or not any lines exist yet.
                render: (row)=>/*#__PURE__*/ _jsx(SpecraButton, {
                        variant: "link",
                        size: "sm",
                        className: "h-auto p-0",
                        // Stop the row's own onClick (which opens the read drawer) from also
                        // firing — this cell has its own drilldown to the staffing drawer.
                        onClick: (e)=>{
                            e.stopPropagation();
                            setStaffingAssignment(row);
                        },
                        "data-testid": Testids.deliveryProgramRel.companyStaffingOpen,
                        children: row.fteCount
                    })
            },
            {
                key: 'vacancies',
                header: 'Vacancies'
            },
            {
                key: 'workshare',
                header: 'Workshare %',
                // Share of the program's active FTE. Inactive rows show '—' (not part of
                // current staffing); '—' also covers a program with no active staffing at
                // all, reading blank rather than a misleading 0% or NaN. A real but
                // sub-1% contributor shows '<1%' instead of rounding down to a '0%' that
                // looks identical to a genuine zero.
                render: (row)=>{
                    if (!row.active || totalFte <= 0) return '—';
                    const pct = (row.fteCount || 0) / totalFte * 100;
                    const rounded = pct.toFixed(0);
                    return pct > 0 && rounded === '0' ? '<1%' : `${rounded}%`;
                }
            },
            {
                key: 'active',
                header: 'Active',
                render: (row)=>row.active ? 'Yes' : 'No'
            }
        ], [
        companyById,
        totalFte,
        linesByAssignment,
        missingPaperwork
    ]);
    // Shared by the create modal and the drawer edit-in-place; the target id is
    // passed in (empty = create), and the selected company is passed explicitly
    // (the create modal reads it from its RHF select; the drawer from parent
    // state) so the retry/assignment-id logic stays identical on both surfaces.
    const submitCompany = async (values, id, companyProfileId)=>{
        // On a retry after a failed document step the assignment already exists —
        // reuse its id so the server updates it rather than inserting a duplicate.
        // Only when the company still matches: the update path ignores
        // companyProfileId, so a changed company must start a fresh assignment.
        const priorAssignment = createdAssignmentRef.current;
        const priorId = priorAssignment?.companyProfileId === companyProfileId ? priorAssignment.id : '';
        const assignment = await upsert.mutateAsync({
            id: id || priorId,
            programId,
            companyProfileId,
            active: Boolean(values.active),
            type: values.type,
            fteCount: Number(values.fteCount) || 0,
            vacancies: Number(values.vacancies) || 0
        });
        if (assignment?.id) {
            createdAssignmentRef.current = {
                id: assignment.id,
                companyProfileId
            };
        }
        // If the user created one company, hit a failed document step, then switched
        // companies and resubmitted, the first company was already committed. Now
        // that its replacement is saved, remove the abandoned one so switching does
        // not leave a stray assignment. Best-effort and only after the new row
        // exists, so a failure here never loses both.
        if (priorAssignment && assignment?.id && priorAssignment.id !== assignment.id && priorAssignment.companyProfileId !== companyProfileId) {
            try {
                await remove.mutateAsync(priorAssignment.id);
            } catch  {
            // Leave the row — it is visible in the table and can be deleted by hand.
            }
        }
        // The file(s) are optional: adding a company must not be blocked on having
        // a document. A picked existing document (fileId) and freshly staged
        // uploads (stagedFiles) are mutually exclusive at the picker level.
        // Staged uploads record as one ProgramDocument PER file, each titled from
        // its own file name.
        const errors = [];
        if (stagedFiles.length > 0) {
            const result = await upload.mutateAsync({
                files: stagedFiles
            });
            const succeededFiles = new Set(result.files.map((f)=>f.file));
            // Move uploaded files out of staging so a retry doesn't re-upload them;
            // failed ones stay staged so the user can retry just those. Succeeded
            // ones move into pendingDocumentsRef until their ProgramDocument is
            // recorded below, so a retry after a record failure replays them
            // instead of re-uploading. Matched by File identity, not name — two
            // staged files can share a name, and a name-based filter would drop
            // both on a partial failure.
            setStagedFiles(stagedFiles.filter((f)=>!succeededFiles.has(f)));
            pendingDocumentsRef.current = [
                ...pendingDocumentsRef.current,
                ...result.files.map((f)=>({
                        fileId: f.artifactId,
                        fileName: f.name
                    }))
            ];
            errors.push(...result.failed.map((f)=>`${f.name}: ${f.error}`));
        }
        const pending = pendingDocumentsRef.current;
        if (pending.length > 0) {
            // The company saved but the server did not echo an id, so the
            // documents cannot be linked. Surface it rather than closing as a
            // silent success that leaves files uploaded but unrecorded.
            if (!assignment?.id) {
                throw new Error('The company was saved but its id was not returned, so the documents were not attached.');
            }
            for (const f of pending){
                try {
                    await createDoc.mutateAsync({
                        programId,
                        companyAssignmentId: assignment.id,
                        title: f.fileName || 'Company document',
                        description: '',
                        // The Add Company form has no doc-date field; stamp the attach
                        // date so the row sorts sensibly rather than landing at the epoch.
                        docDate: new Date(),
                        fileId: f.fileId,
                        // The server resolves the display filename from the artifact and
                        // derives the uploader from the caller — both are ignored here.
                        fileName: '',
                        category: documentCategory,
                        uploadedBy: ''
                    });
                    pendingDocumentsRef.current = pendingDocumentsRef.current.filter((p)=>p !== f);
                } catch (err) {
                    errors.push(`${f.fileName}: ${err instanceof Error ? err.message : 'Failed to record document'}`);
                }
            }
        }
        if (fileId) {
            // The company saved but the server did not echo an id, so the document
            // cannot be linked. Surface it rather than closing as a silent success
            // that leaves the file uploaded but unrecorded.
            if (!assignment?.id) {
                throw new Error('The company was saved but its id was not returned, so the document was not attached.');
            }
            const resolvedName = docsQuery.data?.items.find((d)=>d.fileId === fileId)?.fileName ?? '';
            await createDoc.mutateAsync({
                programId,
                companyAssignmentId: assignment.id,
                title: resolvedName || 'Company document',
                description: '',
                docDate: new Date(),
                fileId,
                fileName: '',
                category: documentCategory,
                uploadedBy: ''
            });
        }
        // Partial upload/record failures are honest: the successes above are
        // already recorded, but the caller must still see what didn't make it.
        if (errors.length > 0) {
            throw new Error(errors.join('; '));
        }
    };
    const handleDelete = async ()=>{
        if (!deleting) return;
        await remove.mutateAsync(deleting.id);
        setDeleting(null);
    };
    // Per-record staffing-line count: once an assignment has lines, fte_count /
    // vacancies are server-derived (SUM of lines / COUNT of contact-less lines),
    // so the drawer shows them read-only with a "why" hint rather than letting a
    // hand-typed edit silently race the derived total on the next save.
    const detailLineCount = detail ? linesByAssignment.get(detail.id)?.length ?? 0 : 0;
    const detailDerivedHint = detailLineCount > 0 ? `Derived from ${detailLineCount} staffing line${detailLineCount === 1 ? '' : 's'}` : undefined;
    const detailWorkshare = (()=>{
        if (!detail || !detail.active || totalFte <= 0) return '—';
        const pct = (detail.fteCount || 0) / totalFte * 100;
        const rounded = pct.toFixed(0);
        return pct > 0 && rounded === '0' ? '<1%' : `${rounded}%`;
    })();
    // Self-labeled company control shared by the drawer's inline edit (dropped into
    // its Company row via editControl). It drives parent state, mirroring how
    // RisksTab's owner picker works — a pick never touches the RHF form. The
    // trigger keeps id="companyProfileId" so it reads as the "Company" field.
    const companyEditControl = /*#__PURE__*/ _jsxs("div", {
        className: "space-y-1.5",
        children: [
            /*#__PURE__*/ _jsx(Label, {
                htmlFor: "companyProfileId",
                children: "Company"
            }),
            /*#__PURE__*/ _jsxs(Select, {
                value: editCompanyProfileId,
                onValueChange: setEditCompanyProfileId,
                children: [
                    /*#__PURE__*/ _jsx(SelectTrigger, {
                        id: "companyProfileId",
                        children: /*#__PURE__*/ _jsx(SelectValue, {
                            placeholder: "Select a company…"
                        })
                    }),
                    /*#__PURE__*/ _jsx(SelectContent, {
                        children: companies.map((c)=>/*#__PURE__*/ _jsx(SelectItem, {
                                value: c.id,
                                children: c.name
                            }, c.id))
                    })
                ]
            })
        ]
    });
    // The drawer's inline-edit config, bound to the viewed record. Company is
    // seeded into parent state on edit start (it's an editControl, not RHF).
    const detailEdit = detail ? {
        defaultValues: {
            id: detail.id,
            companyProfileId: detail.companyProfileId,
            type: detail.type ?? '',
            fteCount: detail.fteCount ?? 0,
            vacancies: detail.vacancies ?? 0,
            active: detail.active ?? true
        },
        onSubmit: (v)=>submitCompany(v, detail.id, editCompanyProfileId),
        isSubmitting: upsert.isPending || upload.isPending || createDoc.isPending || remove.isPending,
        submitLabel: 'Save changes',
        onEditStart: ()=>setEditCompanyProfileId(detail.companyProfileId)
    } : undefined;
    // Read sections plus, per row, the inline-edit control. Company is a custom
    // control (editControl → parent state); the rest are plain editField. FTE and
    // Vacancies disable (with a "why" hint in the read value) once staffing lines
    // exist, since they're server-derived then.
    const detailSections = detail ? [
        {
            heading: 'Assignment',
            rows: [
                {
                    label: 'Company',
                    value: detail.companyName || companyById.get(detail.companyProfileId)?.name || '—',
                    editControl: companyEditControl
                },
                {
                    label: 'Type',
                    value: detail.type || '—',
                    editField: {
                        kind: 'select',
                        name: 'type',
                        label: 'Type',
                        options: typeOptions
                    }
                },
                {
                    label: 'FTE count',
                    value: detailLineCount > 0 ? /*#__PURE__*/ _jsxs("span", {
                        children: [
                            detail.fteCount,
                            ' ',
                            /*#__PURE__*/ _jsxs("span", {
                                className: "text-muted-foreground",
                                children: [
                                    "· ",
                                    detailDerivedHint
                                ]
                            })
                        ]
                    }) : String(detail.fteCount),
                    editField: {
                        kind: 'number',
                        name: 'fteCount',
                        label: 'FTE count',
                        min: 0,
                        step: 0.5,
                        disabled: detailLineCount > 0
                    }
                },
                {
                    label: 'Vacancies',
                    value: detailLineCount > 0 ? /*#__PURE__*/ _jsxs("span", {
                        children: [
                            detail.vacancies,
                            ' ',
                            /*#__PURE__*/ _jsxs("span", {
                                className: "text-muted-foreground",
                                children: [
                                    "· ",
                                    detailDerivedHint
                                ]
                            })
                        ]
                    }) : String(detail.vacancies),
                    editField: {
                        kind: 'number',
                        name: 'vacancies',
                        label: 'Vacancies',
                        min: 0,
                        step: 1,
                        disabled: detailLineCount > 0
                    }
                },
                {
                    label: 'Active',
                    value: detail.active ? 'Yes' : 'No',
                    editField: {
                        kind: 'checkbox',
                        name: 'active',
                        label: 'Active assignment'
                    }
                },
                // Computed from the program's active FTE — read-only (no edit control).
                {
                    label: 'Workshare %',
                    value: detailWorkshare
                }
            ]
        }
    ] : [];
    return /*#__PURE__*/ _jsxs("div", {
        className: "space-y-4",
        children: [
            /*#__PURE__*/ _jsxs("div", {
                className: "flex justify-end gap-2",
                children: [
                    canEditPolicy && ownCompany.data && !primeAssignment && /*#__PURE__*/ _jsx(SpecraButton, {
                        variant: "secondary",
                        onClick: ()=>{
                            handleAddPrime().catch(()=>{
                            // handleAddPrime already toasts the failure; nothing further to do.
                            });
                        },
                        disabled: seedingPrime || upsert.isPending,
                        "data-testid": Testids.deliveryProgramRel.companyAddPrime,
                        children: seedingPrime ? 'Adding…' : `Add ${ownCompany.data.name || 'Your Organization'} as Prime`
                    }),
                    /*#__PURE__*/ _jsx(SpecraButton, {
                        onClick: ()=>{
                            resetFile();
                            setFormOpen(true);
                        },
                        disabled: !canEditPolicy || companies.length === 0,
                        "data-testid": Testids.deliveryProgramRel.companyCreate,
                        children: "Add Company"
                    })
                ]
            }),
            /*#__PURE__*/ _jsx(EntityTable, {
                data: assignments,
                columns: columns,
                testid: Testids.deliveryProgramRel.companiesTable,
                rowTestid: Testids.deliveryProgramRel.companyRow,
                rowKey: (a)=>a.id,
                isLoading: isLoading,
                hideRowActions: !canEditPolicy,
                onRowClick: (row)=>{
                    setDetailMode('read');
                    setDetailId(row.id);
                },
                rowActions: (row)=>/*#__PURE__*/ _jsxs("div", {
                        className: "flex gap-2 justify-end",
                        children: [
                            /*#__PURE__*/ _jsx(SpecraButton, {
                                size: "sm",
                                variant: "ghost",
                                onClick: ()=>setStaffingAssignment(row),
                                "data-testid": Testids.deliveryProgramRel.companyStaffingAction,
                                children: "Staffing"
                            }),
                            /*#__PURE__*/ _jsx(SpecraButton, {
                                size: "sm",
                                variant: "ghost",
                                // Edit-in-place: open the drawer straight into edit mode; the
                                // drawer's onEditStart seeds the company control from this record.
                                onClick: ()=>{
                                    setDetailMode('edit');
                                    setDetailId(row.id);
                                },
                                "data-testid": Testids.deliveryProgramRel.companyEdit,
                                children: "Edit"
                            }),
                            /*#__PURE__*/ _jsx(SpecraButton, {
                                size: "sm",
                                variant: "ghost",
                                className: "text-danger hover:bg-danger-muted hover:text-danger",
                                onClick: ()=>setDeleting(row),
                                "data-testid": Testids.deliveryProgramRel.companyDelete,
                                children: "Delete"
                            })
                        ]
                    })
            }),
            staffingAssignmentLive && /*#__PURE__*/ _jsx(CompanyStaffingDrawer, {
                programId: programId,
                assignment: staffingAssignmentLive,
                onClose: ()=>setStaffingAssignment(null)
            }),
            /*#__PURE__*/ _jsx(EntityFormDialog, {
                open: formOpen,
                onOpenChange: setFormOpen,
                title: "Add Company to Program",
                fields: createFields,
                defaultValues: {
                    id: '',
                    companyProfileId: '',
                    type: '',
                    fteCount: 0,
                    vacancies: 0,
                    active: true
                },
                extraContent: /*#__PURE__*/ _jsxs("div", {
                    className: "space-y-2",
                    children: [
                        /*#__PURE__*/ _jsx(ProgramDocumentPicker, {
                            id: "companyFileId",
                            label: "Document (optional)",
                            helpText: "Pick a file from the documents already recorded on this program.",
                            value: fileId,
                            onChange: (id)=>{
                                setFileId(id);
                                if (id) setStagedFiles([]);
                            },
                            programId: programId
                        }),
                        /*#__PURE__*/ _jsx("p", {
                            className: "text-xs text-muted-foreground",
                            children: "…or upload a new file:"
                        }),
                        /*#__PURE__*/ _jsx(FileUploader, {
                            files: stagedFiles,
                            onChange: (f)=>{
                                setStagedFiles(f);
                                if (f.length > 0) setFileId('');
                            },
                            disabled: upload.isPending || createDoc.isPending,
                            inputTestid: Testids.deliveryProgramRel.companyUploadInput
                        }),
                        /*#__PURE__*/ _jsxs("div", {
                            className: "space-y-1.5",
                            children: [
                                /*#__PURE__*/ _jsx(Label, {
                                    htmlFor: "companyDocumentCategory",
                                    children: "Category"
                                }),
                                /*#__PURE__*/ _jsxs(Select, {
                                    value: documentCategory,
                                    onValueChange: setDocumentCategory,
                                    children: [
                                        /*#__PURE__*/ _jsx(SelectTrigger, {
                                            id: "companyDocumentCategory",
                                            "data-testid": Testids.deliveryProgramRel.companyDocumentCategory,
                                            children: /*#__PURE__*/ _jsx(SelectValue, {})
                                        }),
                                        /*#__PURE__*/ _jsx(SelectContent, {
                                            children: DOCUMENT_CATEGORY_OPTIONS.map((o)=>/*#__PURE__*/ _jsx(SelectItem, {
                                                    value: o.value,
                                                    children: o.label
                                                }, o.value))
                                        })
                                    ]
                                })
                            ]
                        })
                    ]
                }),
                onSubmit: async (values)=>{
                    await submitCompany(values, '', values.companyProfileId);
                    setFormOpen(false);
                    resetFile();
                },
                isSubmitting: upsert.isPending || upload.isPending || createDoc.isPending || remove.isPending
            }),
            /*#__PURE__*/ _jsx(ConfirmDeleteDialog, {
                open: !!deleting,
                onOpenChange: (open)=>!open && setDeleting(null),
                title: "Remove Company",
                description: deleting ? `Remove "${deleting.companyName}" from this program? This cannot be undone.` : undefined,
                onConfirm: handleDelete,
                isPending: remove.isPending
            }),
            /*#__PURE__*/ _jsx(ScoreDetailDrawer, {
                open: !!detail,
                onOpenChange: (open)=>!open && closeDetail(),
                title: detail ? detail.companyName || companyById.get(detail.companyProfileId)?.name || 'Company' : '',
                sections: detailSections,
                testid: Testids.deliveryProgramRel.companyDetailDrawer,
                edit: detailEdit,
                canEdit: canEditPolicy,
                initialMode: detailMode,
                children: detail && /*#__PURE__*/ _jsx(DrawerDocumentsSection, {
                    programId: programId,
                    scope: {
                        companyAssignmentId: detail.id
                    },
                    emptyText: "No documents recorded for this company.",
                    canEdit: canEditPolicy
                })
            })
        ]
    });
}
